import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Skeleton } from '@mui/material';

import { useAuth } from '../../shared/context';
import { useDealProductQuery, useDealQuery, useDealRequiredDocumentsQuery } from '../../shared/hooks/Queries';
import { LoanApplicationTask, LoanApplicationTaskDocument } from '../../shared/models';
import {
	CerebroBreadcrumb,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroStyledBox,
	CerebroText,
} from '../../shared/components';

import { DeleteDocumentDialog } from './DeleteDocumentDialog';
import { UploadDocumentDrawer } from './UploadDocumentDrawer';
import { RequestDocumentDrawer } from './RequestDocumentDrawer';
import { LoanApplicationDocument } from './LoanApplicationDocument';
import { DeleteDocumentFileDialog } from './DeleteDocumentFileDialog';
import { MarkDocumentAsApplicableDialog } from './MarkDocumentAsApplicableDialog';
import { MarkDocumentAsNotApplicableDialog } from './MarkDocumentAsNotApplicableDialog';
import { MarkDocumentAsCompleteDialog } from './MarkDocumentAsCompleteDialog';
import { useNavigateToNextPage } from '../../shared/hooks';
import { DocumentActionButton } from './DocumentActionButton';
import { ReorderDocumentListDrawer } from './ReorderDocumentListDrawer';
import { UploadExampleTemplateDrawer } from './UploadExampleTemplateDrawer';
import { EditDocumentDrawer } from './EditDocumentDrawer';

export const Documents: React.FC = () => {
	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();
	const navigateToNextPage = useNavigateToNextPage();

	const documentsQuery = useDealRequiredDocumentsQuery(guid);
	const productQuery = useDealProductQuery(guid);
	const dealQuery = useDealQuery(guid);

	const docOnlyView =
		!productQuery.data?.isContractRequired &&
		dealQuery.data?.dealStatus === 'Accepted' &&
		!currentUser.isEmployeeUser &&
		(documentsQuery.data?.length ?? 0) > 0;
	const pendindRequiredDocuments =
		documentsQuery.data?.filter(document => !document.notApplicable && !document.completedOn) ?? [];

	const showContinueButton = !docOnlyView || pendindRequiredDocuments.length == 0;

	const [action, setAction] = React.useState<RequiredDocumentsAction | null>(null);
	const [showReorder, setShowReorder] = React.useState(false);
	const onClose = () => {
		setAction(null);
		setShowReorder(false);
	};

	console.log('action', action);

	return (
		<>
			<CerebroPageContainer>
				<CerebroBreadcrumb>Required Documents</CerebroBreadcrumb>

				{documentsQuery.isLoading && <Skeleton height={400} />}

				{documentsQuery.isSuccess && (
					<Grid container spacing={2} alignItems="stretch">
						{documentsQuery.data.length === 0 && (
							<Grid item xs={12}>
								<CerebroStyledBox sx={{ marginBottom: 2, height: '100%' }}>
									<CerebroText sx={{ marginBottom: 2 }}>
										{currentUser && currentUser.isEmployeeUser
											? 'We have not requested any documents from the borrower.'
											: 'Required Documents will appear depending on how you fill out the loan application.'}
									</CerebroText>
								</CerebroStyledBox>
							</Grid>
						)}

						{currentUser && currentUser.isEmployeeUser && (
							<Grid item xs={12}>
								<DocumentActionButton onAdd={setAction} onReorder={setShowReorder} />
							</Grid>
						)}

						{documentsQuery.data
							.sort(
								(a, b) =>
									a.statusValue - b.statusValue ||
									a.loanApplicationDisplayOrder - b.loanApplicationDisplayOrder
							)
							.map(document => (
								<Grid item key={document.guid} xs={12}>
									<LoanApplicationDocument
										task={document}
										dealGuid={guid}
										onUpload={() => setAction({ type: 'Upload', document })}
										onEdit={() => setAction({ type: 'Edit', document })}
										onMarkNa={() => setAction({ type: 'Mark as N/A', document })}
										onClearNa={() => setAction({ type: 'Clear N/A', document })}
										onDelete={() => setAction({ type: 'Delete', document })}
										onDeleteFile={file => setAction({ type: 'Delete File', document, file })}
										onUploadExample={() => setAction({ type: 'Upload Example', document })}
									/>
								</Grid>
							))}
					</Grid>
				)}

				{showContinueButton && (
					<CerebroPrimaryButton sx={{ marginTop: 6 }} onClick={() => navigateToNextPage()}>
						Continue
					</CerebroPrimaryButton>
				)}
			</CerebroPageContainer>

			{action && action.type === 'Upload' && (
				<UploadDocumentDrawer open onClose={onClose} document={action.document} />
			)}

			{action && action.type === 'Edit' && (
				<EditDocumentDrawer open onClose={onClose} document={action.document} />
			)}

			{action && action.type === 'Upload Example' && (
				<UploadExampleTemplateDrawer open onClose={onClose} document={action.document} />
			)}

			{currentUser != null && currentUser.isEmployeeUser && action && action.type == 'Request' && (
				<RequestDocumentDrawer open onClose={onClose} dataRoomGuid={dealQuery.data?.dataRoomGuid} />
			)}

			{currentUser != null && currentUser.isEmployeeUser && action && action.type === 'Delete' && (
				<DeleteDocumentDialog open onClose={onClose} document={action.document} />
			)}

			{action && action.type === 'Mark as N/A' && (
				<MarkDocumentAsNotApplicableDialog open onClose={onClose} document={action.document} />
			)}

			{action && action.type === 'Clear N/A' && (
				<MarkDocumentAsApplicableDialog open onClose={onClose} document={action.document} />
			)}

			{action && action.type === 'Delete File' && (
				<DeleteDocumentFileDialog open onClose={onClose} document={action.document} file={action.file} />
			)}

			{action && action.type === 'Mark Complete' && (
				<MarkDocumentAsCompleteDialog open onClose={onClose} document={action.document} />
			)}

			<ReorderDocumentListDrawer
				dealGuid={guid}
				documents={documentsQuery.data}
				open={showReorder}
				onClose={onClose}
			/>
		</>
	);
};

export type RequiredDocumentsAction =
	| {
			type: 'Upload' | 'Edit' | 'Delete' | 'Mark as N/A' | 'Clear N/A' | 'Mark Complete' | 'Upload Example';
			document: LoanApplicationTask;
	  }
	| {
			type: 'Delete File';
			document: LoanApplicationTask;
			file: LoanApplicationTaskDocument;
	  }
	| { type: 'Request' };
